import React, { FC, ReactNode, useState } from 'react';
import { PoolSizeAdjustmentReason } from 'shared';
import { twMerge } from 'tailwind-merge';

import { IconProps } from '../../../../assets/icons/icon.types';
import { FileLinesIcon, PencilLineIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { StringKey } from '../../../../lang/index';
import { AdjustPoolSizeFooter } from '../components/modal-footer';

const reasonOptions: {
  description: StringKey;
  icon: FC<IconProps>;
  reason: PoolSizeAdjustmentReason;
  title: StringKey;
}[] = [
  {
    reason: PoolSizeAdjustmentReason.DATA_ENTRY_CORRECTION,
    icon: PencilLineIcon,
    title: 'FIX_DATA_ENTRY_ERROR',
    description: 'FIX_DATA_ENTRY_ERROR_DESCRIPTION',
  },
  {
    reason: PoolSizeAdjustmentReason.FORMAL_ADJUSTMENT,
    icon: FileLinesIcon,
    title: 'RECORD_FORMAL_ADJUSTMENT',
    description: 'RECORD_FORMAL_ADJUSTMENT_DESCRIPTION',
  },
];

const colorTransitionClassName = 'transition-colors duration-200 motion-reduce:transition-none';

type ReasonOptionCardProps = {
  description: ReactNode;
  icon: FC<IconProps>;
  isSelected: boolean;
  onSelect: () => void;
  title: ReactNode;
};

const ReasonOptionCard: FC<ReasonOptionCardProps> = ({
  description,
  icon: Icon,
  isSelected,
  onSelect,
  title,
}) => (
  <button
    aria-checked={isSelected}
    // An inset ring is the design's inside stroke: it keeps the card at its padded size.
    className={twMerge(
      'flex flex-1 flex-col items-start justify-center gap-2 px-4 py-3 text-left ring-1 ring-inset transition-[background-color,box-shadow,border-radius] duration-200 motion-reduce:transition-none',
      isSelected
        ? 'rounded bg-brand-25 ring-brand-700'
        : 'rounded-lg bg-white ring-gray-100 hover:ring-gray-200',
    )}
    onClick={onSelect}
    role="radio"
    type="button"
  >
    {/* Drawn in `currentColor`, so the icon fades between colours with the text. */}
    <Icon
      className={twMerge(colorTransitionClassName, isSelected ? 'text-brand-700' : 'text-gray-700')}
      iconColor="currentColor"
    />
    <span className="flex flex-col">
      <span
        className={twMerge(
          'text-sm',
          colorTransitionClassName,
          isSelected ? 'font-[550] text-brand-700' : 'font-[450] text-gray-700',
        )}
      >
        {title}
      </span>
      {/* Both states keep an 18px line (the design draws the selected one at 16px), so picking a
          card restyles it without changing its height. */}
      <span
        className={twMerge(
          colorTransitionClassName,
          isSelected
            ? 'text-xs font-[450] leading-[18px] text-brand-600'
            : '!text-label-md font-medium text-gray-400',
        )}
      >
        {description}
      </span>
    </span>
  </button>
);

export type ReasonStepProps = {
  /** The reason of the path Back returned from, so going back does not discard the choice. */
  initialReason?: null | PoolSizeAdjustmentReason;
  onCancel: () => void;
  onContinue: (reason: PoolSizeAdjustmentReason) => void;
};

export const ReasonStep: FC<ReasonStepProps> = ({ initialReason = null, onCancel, onContinue }) => {
  const [selectedReason, setSelectedReason] = useState<null | PoolSizeAdjustmentReason>(
    initialReason,
  );

  return (
    <>
      <div className="flex w-full flex-col gap-2">
        <span
          className="flex min-h-8 items-center py-1 text-xs font-[450] text-gray-700"
          id="adjust-pool-size-reason"
        >
          <AppFormattedMessage id="POOL_SIZE_CHANGE_REASON" />
        </span>
        <div
          aria-labelledby="adjust-pool-size-reason"
          className="flex items-center gap-4"
          role="radiogroup"
        >
          {reasonOptions.map(({ reason, icon, title, description }) => (
            <ReasonOptionCard
              description={<AppFormattedMessage id={description} />}
              icon={icon}
              isSelected={selectedReason === reason}
              key={reason}
              onSelect={() => setSelectedReason(reason)}
              title={<AppFormattedMessage id={title} />}
            />
          ))}
        </div>
      </div>
      <AdjustPoolSizeFooter
        confirmLabel={<AppFormattedMessage id="CONTINUE" />}
        isConfirmDisabled={!selectedReason}
        onCancel={onCancel}
        onConfirm={() => selectedReason && onContinue(selectedReason)}
      />
    </>
  );
};
